import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import ILog, { newLog } from '@/models/LogData';
import IErrorLogMsg, { newErrorLogMsg } from '@/models/logging/IErrorLogMsg';
import LogService from '@/services/LogService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import JWTUtils from '@/utils/JWTUtils';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import URLUtils from '@/utils/URLUtils';

/**
 * Manager for generating and populating usage log messages and sending them through the Log service.
 */
export default {
    /**
     * Create a basic log message with the given event id and message.
     * Populates the customer number, passport id, url, ip address and
     * identifier properties.
     * The first data string will contain the local storage value for key "qs-uuid".
     * Provides optional setting of the first data numeric.
     * @see setStrings() and @see setNumerics() for how to set second and third data values.
     * @returns
     */
    createLogMsg(eventId: number, message: string, firstNumeric?: number): ILog {
        const qsAccessToken = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);
        const customerNumberString = JWTUtils.getCustomerNumber(qsAccessToken ?? '');
        const passportIdString = JWTUtils.getPassportId(qsAccessToken ?? '');
        const p2id = theLocalStorageUtils.get(LocalStorageKeys.p2id);
        const ipAddress = JWTUtils.getIpAddress(qsAccessToken ?? '');
        const qsGuid = theLocalStorageUtils.get(LocalStorageKeys.uuid);

        const logMsg = newLog();
        logMsg.eventId = eventId;
        logMsg.customerNumber = Number(customerNumberString);
        logMsg.passportId = Number(passportIdString);
        logMsg.url = URLUtils.getURL();
        logMsg.ipAddress = ipAddress;
        logMsg.identifier = p2id ?? '';
        logMsg.message = message;
        logMsg.dataString1 = qsGuid ?? '';

        if (firstNumeric) {
            logMsg.dataNumeric1 = firstNumeric;
        }

        return logMsg;
    },

    /**
     * Create a basic error message with the given http response code and message.
     * Populates the data string with the qsGuid (eg. uuid).
     * @param httpResponseCode
     * @param message
     * @returns
     */
    createErrorMsg(httpResponseCode: number, message: string): IErrorLogMsg {
        const qsGuid = theLocalStorageUtils.get(LocalStorageKeys.uuid);

        const errorMsg = newErrorLogMsg();
        errorMsg.httpResponseCode = httpResponseCode;
        errorMsg.message = message;
        errorMsg.dataString = qsGuid ?? '';

        return errorMsg;
    },

    /**
     * Set the second and optionally the third data numeric value(s) to the given log message.
     * @param logMsg
     * @param second
     * @param third
     */
    setNumerics(logMsg: ILog, second: number, third?: number) {
        logMsg.dataNumeric2 = second;

        if (third) {
            logMsg.dataNumeric3 = third;
        }
    },

    /**
     * Set the second and optionally the third data string value(s) to the given log message.
     * @param logMsg
     * @param second
     * @param third
     */
    setStrings(logMsg: ILog, second: string, third?: string) {
        logMsg.dataString2 = second;

        if (third) {
            logMsg.dataString3 = third;
        }
    },

    async sendUsage(logMsg: ILog): Promise<boolean> {
        const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
        const service = new LogService(webEnvConfig);
        const success = await service.saveUsageLog(logMsg);
        if (!success) {
            console.log('LogManager.sendUsage(), Failed, bad response');
        }
        return success;
    },

    async sendError(errorMsg: IErrorLogMsg): Promise<boolean> {
        const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
        const service = new LogService(webEnvConfig);
        const success = await service.saveErrorLog(errorMsg);
        if (!success) {
            console.log('LogManager.sendError(), Failed, bad response');
        }
        return success;
    }
};
